<template>
  <div class="admin-page">
    <section class="apps-section d-flex flex-column">
      <div class="section-header admin-header justify-center">
        <div>
          <p class="section-label">Administração</p>
          <h3>Acessos</h3>
        </div>

        <div class="admin-header-actions flex-grow-1 justify-end">
          <v-text-field
            v-model="search"
            label="Pesquisar"
            density="compact"
            variant="outlined"
            hide-details
            clearable
            style="max-width: 280px;"
            @update:model-value="onSearchChange"
          />

          <v-btn
            color="primary"
            icon
            @click="openCreateDialog"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>
      </div>

      <v-card class="admin-card">
        <v-data-table-server
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :items="appAccessesList"
          :items-length="totalAppAccesses"
          :loading="loading"
          :items-per-page-options="[5, 10, 15, 20]"
          item-value="id"
          @update:options="loadAppAccessesList"
        >
          <template #item.created_at="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <template #item.updated_at="{ item }">
            {{ formatDate(item.updated_at) }}
          </template>

          <template #item.actions="{ item }">
            <div class="row-actions">
              <v-btn
                icon
                variant="text"
                @click="openEditDialog(item)"
              >
                <v-icon>mdi-eye</v-icon>
              </v-btn>

              <v-tooltip text="Gerir Roles do acesso/aplicação">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-shield-edit"
                    color="success"
                    variant="text"
                    @click="openManageRolesDialog(item)"
                  />
                </template>
              </v-tooltip>

              <ui-icon-remove @on-submit="removeItem(item.id)" />
            </div>
          </template>

          <template #no-data>
            <div class="empty-state">
              <div class="empty-icon">
                i
              </div>

              <h4>Sem resultados</h4>
              <p>Nenhum acesso encontrado.</p>
            </div>
          </template>
        </v-data-table-server>
      </v-card>

      <!-- Criar / editar acesso -->
      <v-dialog
        v-model="showCreateEditAccessDialog"
        max-width="720"
      >
        <v-card class="dialog-card">
          <v-card-title>
            {{ isEditing ? "Editar acesso" : "Criar acesso" }}
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  label="Id da aplicação"
                  variant="outlined"
                  density="comfortable"
                  :disabled="loadingAccess"
                  :loading="loadingAccess"
                  :readonly="isEditing"
                  hint="Depois de definido, este Id não pode ser alterado."
                  persistent-hint
                  :error-messages="errors.name"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.role"
                  label="Nome do acesso / aplicação"
                  variant="outlined"
                  density="comfortable"
                  :disabled="loadingAccess"
                  :loading="loadingAccess"
                  :error-messages="errors.role"
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="form.url"
                  label="Frontend (URL)"
                  variant="outlined"
                  density="comfortable"
                  :disabled="loadingAccess"
                  :loading="loadingAccess"
                  :error-messages="errors.url"
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="form.backend"
                  label="Backend (URL)"
                  variant="outlined"
                  density="comfortable"
                  :disabled="loadingAccess"
                  :loading="loadingAccess"
                  :error-messages="errors.backend"
                />
              </v-col>
            </v-row>

            <div
              v-if="submitError"
              class="submit-error"
            >
              {{ submitError }}
            </div>

            <v-row class="ma-0">
              <v-col cols="12">
                <div class="permissions-toolbar">
                  <span>
                    <strong>Roles padrão / por defeito</strong>
                  </span>

                  <v-btn
                    color="primary"
                    variant="tonal"
                    prepend-icon="mdi-refresh"
                    :disabled="
                      !canFetchAppPermissions
                        || loadingAccess
                        || isFetchingAppRoles
                        || isFetchingAppPermissions
                    "
                    :loading="
                      isFetchingAppRoles
                        || isFetchingAppPermissions
                    "
                    @click="fetchAppRoles(editingId)"
                  >
                    Atualizar roles e permissões
                  </v-btn>
                </div>

                <v-data-table-server
                  class="permissions-table"
                  density="compact"
                  :class="{
                    'permissions-table--disabled':
                      !canFetchAppPermissions || loadingAccess,
                  }"
                  :headers="permissionsTableHeaders"
                  :items="permissionsTableRows"
                  :items-length="permissionsTableRows.length"
                  :items-per-page="permissionsTableRows.length || 1"
                  :loading="isFetchingAppPermissions"
                  item-value="id"
                  hide-default-footer
                >
                  <template #item.selected="{ item }">
                    <v-checkbox-btn
                      v-if="item.type === 'role'"
                      :model-value="isRoleSelected(item.role_id)"
                      :disabled="
                        !canFetchAppPermissions
                          || loadingAccess
                          || isFetchingAppPermissions
                      "
                      @click.stop
                      @update:model-value="
                        toggleDefaultRole(item, $event)
                      "
                    />
                  </template>

                  <template #item.name="{ item }">
                    <div
                      v-if="item.type === 'role'"
                      class="permissions-role-cell"
                    >
                      <v-btn
                        class="permissions-toggle"
                        :icon="
                          isRoleGroupExpanded(item.role_id)
                            ? 'mdi-chevron-down'
                            : 'mdi-chevron-right'
                        "
                        variant="text"
                        density="compact"
                        size="small"
                        :disabled="
                          loadingAccess
                            || isFetchingAppPermissions
                        "
                        @click.stop="toggleRoleGroup(item.role_id)"
                      />

                      <span class="permissions-role-name">
                        {{ item.name }}
                      </span>
                    </div>

                    <span
                      v-else
                      class="permissions-permission-name"
                    >
                      {{ item.name }}
                    </span>
                  </template>

                  <template #item.display_name="{ item }">
                    <span
                      :class="{
                        'permissions-role-display-name':
                          item.type === 'role',
                        'permissions-permission-display-name':
                          item.type === 'permission',
                      }"
                    >
                      {{ item.display_name }}
                    </span>
                  </template>

                  <template #no-data>
                    <div class="empty-state permissions-empty-state">
                      <h4>Sem permissões</h4>
                    </div>
                  </template>
                </v-data-table-server>

                <div
                  v-if="errors.defaultRoles"
                  class="submit-error"
                >
                  {{ errors.defaultRoles }}
                </div>
              </v-col>
            </v-row>

            <v-alert
              v-if="grantAllUsersSuccess"
              type="success"
              variant="tonal"
              class="mt-4"
            >
              {{ grantAllUsersSuccess }}
            </v-alert>

            <v-alert
              v-if="grantAllUsersError"
              type="error"
              variant="tonal"
              class="mt-4"
            >
              {{ grantAllUsersError }}
            </v-alert>
          </v-card-text>

          <v-card-actions class="access-dialog-actions">
            <v-btn
              v-if="isEditing && editingId"
              color="success"
              variant="tonal"
              prepend-icon="mdi-account-multiple-plus"
              :loading="grantingAccessToAllUsers"
              :disabled="
                grantingAccessToAllUsers
                  || submitting
                  || loadingAccess
                  || isFetchingAppRoles
                  || isFetchingAppPermissions
              "
              @click="openGrantAllUsersConfirmation"
            >
              Dar acesso a todos os users
            </v-btn>

            <v-spacer />

            <v-btn
              variant="text"
              :disabled="grantingAccessToAllUsers"
              @click="closeDialog"
            >
              Cancelar
            </v-btn>

            <v-btn
              color="primary"
              :loading="submitting"
              :disabled="
                submitting
                  || grantingAccessToAllUsers
                  || loadingAccess
                  || isFetchingAppRoles
                  || isFetchingAppPermissions
              "
              @click="submitForm"
            >
              {{ isEditing ? "Guardar" : "Criar" }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirmação para dar acesso a todos -->
      <v-dialog
        v-model="showGrantAllUsersConfirmation"
        max-width="520"
        persistent
      >
        <v-card class="dialog-card">
          <v-card-title>
            Confirmar atribuição de acesso
          </v-card-title>

          <v-card-text>
            <p class="confirmation-text">
              Tem a certeza que pretende dar acesso ao
              <strong>{{ selectedAccessLabel }}</strong>
              a todos os users?
            </p>

            <v-alert
              type="warning"
              variant="tonal"
              class="mt-4"
            >
              Esta operação irá associar este acesso a todos os users e
              atribuir-lhes a respetiva role no Keycloak.
            </v-alert>

            <div
              v-if="grantAllUsersConfirmationError"
              class="submit-error mt-4"
            >
              {{ grantAllUsersConfirmationError }}
            </div>
          </v-card-text>

          <v-card-actions class="justify-end">
            <v-btn
              variant="text"
              :disabled="grantingAccessToAllUsers"
              @click="closeGrantAllUsersConfirmation"
            >
              Não
            </v-btn>

            <v-btn
              color="success"
              prepend-icon="mdi-check"
              :loading="grantingAccessToAllUsers"
              :disabled="grantingAccessToAllUsers"
              @click="grantAccessToAllUsers"
            >
              Sim
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </section>

    <section class="apps-section users-wrapper">
      <UsersAdminView />
    </section>

    <CentralCostsTable />
  </div>
</template>

<script setup>
import {
    computed,
    onMounted,
    reactive,
    ref,
    watch,
} from "vue";

import Accesses from "@/api/Accesses";
import AccessRoles from "@/api/AccessRoles";
import AccessRolesManagementTable from "@/components/accesses/AccessRolesManagementTable.vue";
import UiIconRemove from "@/components/ui/IconRemove.vue";
import UsersAdminView from "@/components/Users/UsersAdminView.vue";
import CentralCostsTable from "@/components/central_costs/CentralCosts.vue";
import { useDialog } from "@internal_projects_dd/universal-dialog";

defineOptions({
    name: "AdminPanelView",
});

const loading = ref(false);
const loadingAccess = ref(false);
const submitting = ref(false);

const showCreateEditAccessDialog = ref(false);
const isEditing = ref(false);
const editingId = ref(null);

const showGrantAllUsersConfirmation = ref(false);
const grantingAccessToAllUsers = ref(false);
const grantAllUsersConfirmationError = ref("");
const grantAllUsersSuccess = ref("");
const grantAllUsersError = ref("");

const appAccessesList = ref([]);
const totalAppAccesses = ref(0);

const page = ref(1);
const itemsPerPage = ref(10);
const search = ref("");

const submitError = ref("");

const form = reactive(createEmptyForm());
const errors = reactive(createEmptyErrors());

const isFetchingAppRoles = ref(false);
const isFetchingAppPermissions = ref(false);

const appPermissions = ref({});
const expandedRoleGroups = ref({});

const dialog = useDialog();

const headers = [
    {
        title: "Nome",
        key: "name",
    },
    {
        title: "URL",
        key: "url",
    },
    {
        title: "Backend",
        key: "backend",
    },
    {
        title: "Role",
        key: "role",
    },
    {
        title: "Criado em",
        key: "created_at",
    },
    {
        title: "Atualizado em",
        key: "updated_at",
    },
    {
        title: "",
        key: "actions",
        sortable: false,
        width: 120,
    },
];

const permissionsTableHeaders = [
    {
        title: "",
        key: "selected",
        sortable: false,
        width: 56,
    },
    {
        title: "Nome",
        key: "name",
    },
    {
        title: "Descrição",
        key: "display_name",
    },
];

const canFetchAppPermissions = computed(() => {
    return Boolean(
        editingId.value
        && form.backend?.trim(),
    );
});

const selectedAccessLabel = computed(() => {
    return (
        form.role?.trim()
        || form.name?.trim()
        || "acesso selecionado"
    );
});

const formDefaultRoles = computed({
    get() {
        return (
            form.defaultRoles
            && !Array.isArray(form.defaultRoles)
        )
            ? form.defaultRoles
            : {};
    },

    set(value) {
        form.defaultRoles = value;
    },
});

const permissionsTableRows = computed(() => {
    return Object.values(appPermissions.value)
        .flatMap((role) => {
            const permissions = Array.isArray(role.permissions)
                ? role.permissions
                : [];

            const roleId = normalizeRoleId(role.id);

            const roleRow = {
                id: `role:${roleId}`,
                role_id: roleId,
                type: "role",
                name: role.name,
                display_name: role.display_name,
                permissions,
                is_default: normalizeBoolean(role.is_default),
            };

            if (! isRoleGroupExpanded(roleId)) {
                return [roleRow];
            }

            return [
                roleRow,

                ...permissions.map((permission) => ({
                    id: `permission:${roleId}:${permission.id}`,
                    type: "permission",
                    role_id: roleId,
                    role_name: role.name,
                    name: permission.name,
                    display_name: permission.display_name,
                })),
            ];
        });
});

watch(
    () => form.backend,
    (backend) => {
        if (backend?.trim()) {
            return;
        }

        clearAppRolesAndPermissions();
    },
);

onMounted(() => {
    loadAppAccessesList({
        page: page.value,
        itemsPerPage: itemsPerPage.value,
    });
});

function createEmptyForm() {
    return {
        name: "",
        url: "",
        backend: "",
        role: "",
        defaultRoles: {},
    };
}

function createEmptyErrors() {
    return {
        name: "",
        url: "",
        backend: "",
        role: "",
        defaultRoles: "",
    };
}

function resetForm() {
    Object.assign(
        form,
        createEmptyForm(),
    );
}

function resetErrors() {
    Object.assign(
        errors,
        createEmptyErrors(),
    );
}

function resetGrantAllUsersState() {
    showGrantAllUsersConfirmation.value = false;
    grantingAccessToAllUsers.value = false;
    grantAllUsersConfirmationError.value = "";
    grantAllUsersSuccess.value = "";
    grantAllUsersError.value = "";
}

async function loadAppAccessesList(options = {}) {
    loading.value = true;

    try {
        const nextPage =
            options.page
            || page.value;

        const nextItemsPerPage =
            options.itemsPerPage
            || itemsPerPage.value;

        page.value = nextPage;
        itemsPerPage.value = nextItemsPerPage;

        const response = await Accesses.search({
            page: page.value,
            limit: itemsPerPage.value,
            search: search.value,
        });

        appAccessesList.value =
            Array.isArray(response?.data?.data)
                ? response.data.data
                : (
                    Array.isArray(response?.data)
                        ? response.data
                        : []
                );

        totalAppAccesses.value =
            response?.data?.total
            || appAccessesList.value.length
            || 0;
    } catch (error) {
        console.error(
            "Erro ao carregar accesses:",
            error?.response?.data || error,
        );
    } finally {
        loading.value = false;
    }
}

function onSearchChange() {
    page.value = 1;

    loadAppAccessesList({
        page: 1,
        itemsPerPage: itemsPerPage.value,
    });
}

function openCreateDialog() {
    isEditing.value = false;
    editingId.value = null;
    loadingAccess.value = false;
    submitError.value = "";

    resetErrors();
    resetForm();
    resetGrantAllUsersState();
    clearAppRolesAndPermissions();

    showCreateEditAccessDialog.value = true;
}

async function openEditDialog(access = {}) {
    editingId.value = access?.id || null;
    loadingAccess.value = true;
    submitError.value = "";

    resetErrors();
    resetForm();
    resetGrantAllUsersState();
    clearAppRolesAndPermissions();

    isEditing.value = true;
    showCreateEditAccessDialog.value = true;

    Object.assign(form, {
        name: access?.name || "",
        url: access?.url || "",
        backend: access?.backend || "",
        role: access?.role || "",
        defaultRoles: {},
    });

    try {
        if (access?.id && access?.backend) {
            await fetchAppRoles(access.id);
        }
    } catch (error) {
        console.error(
            "Erro a obter os roles da aplicação / acesso:",
            error?.response?.data || error,
        );
    } finally {
        loadingAccess.value = false;
    }
}

async function removeItem(id) {
    try {
        await Accesses.delete(id);

        await loadAppAccessesList({
            page: page.value,
            itemsPerPage: itemsPerPage.value,
        });
    } catch (error) {
        console.error(
            "Erro ao remover access:",
            error?.response?.data || error,
        );
    }
}

function closeDialog() {
    if (grantingAccessToAllUsers.value) {
        return;
    }

    showCreateEditAccessDialog.value = false;
    loadingAccess.value = false;
    submitError.value = "";

    resetErrors();
    resetForm();
    resetGrantAllUsersState();
    clearAppRolesAndPermissions();
}

function validateForm() {
    resetErrors();

    let valid = true;

    if (! form.name?.trim()) {
        errors.name = "O nome é obrigatório.";
        valid = false;
    }

    if (! form.url?.trim()) {
        errors.url = "A URL é obrigatória.";
        valid = false;
    }

    if (! form.backend?.trim()) {
        errors.backend = "O backend é obrigatório.";
        valid = false;
    }

    if (! form.role?.trim()) {
        errors.role = "A role é obrigatória.";
        valid = false;
    }

    return valid;
}

async function submitForm() {
    if (! validateForm()) {
        return;
    }

    submitting.value = true;
    submitError.value = "";

    try {
        const payload = {
            name: form.name.trim(),
            url: form.url.trim(),
            backend: form.backend.trim(),
            role: form.role.trim(),

            default_roles: Object.keys(
                formDefaultRoles.value,
            ),
        };

        if (
            isEditing.value
            && editingId.value
        ) {
            delete payload.name;

            await Accesses.update(
                payload,
                editingId.value,
            );
        } else {
            await Accesses.create(payload);
        }

        showCreateEditAccessDialog.value = false;

        await loadAppAccessesList({
            page: page.value,
            itemsPerPage: itemsPerPage.value,
        });
    } catch (error) {
        const responseData =
            error?.response?.data;

        if (responseData?.errors) {
            errors.name =
                responseData.errors.name?.[0]
                || "";

            errors.url =
                responseData.errors.url?.[0]
                || "";

            errors.backend =
                responseData.errors.backend?.[0]
                || "";

            errors.role =
                responseData.errors.role?.[0]
                || "";

            errors.defaultRoles =
                responseData.errors.default_roles?.[0]
                || "";
        } else {
            submitError.value =
                responseData?.message
                || "Ocorreu um erro ao guardar.";
        }

        console.error(
            "Erro ao guardar access:",
            responseData || error,
        );
    } finally {
        submitting.value = false;
    }
}

function openGrantAllUsersConfirmation() {
    if (
        ! isEditing.value
        || ! editingId.value
    ) {
        return;
    }

    grantAllUsersConfirmationError.value = "";
    grantAllUsersSuccess.value = "";
    grantAllUsersError.value = "";

    showGrantAllUsersConfirmation.value = true;
}

function closeGrantAllUsersConfirmation() {
    if (grantingAccessToAllUsers.value) {
        return;
    }

    showGrantAllUsersConfirmation.value = false;
    grantAllUsersConfirmationError.value = "";
}

async function grantAccessToAllUsers() {
    if (! editingId.value) {
        grantAllUsersConfirmationError.value =
            "Não foi possível identificar o acesso.";

        return;
    }

    grantingAccessToAllUsers.value = true;
    grantAllUsersConfirmationError.value = "";
    grantAllUsersSuccess.value = "";
    grantAllUsersError.value = "";

    try {
        /*
         * Usa a instância Axios e o URL já definidos na classe Accesses,
         * sem ser necessário alterar o ficheiro da API.
         */
        const response = await Accesses
            .getAxiosInstance()
            .post(
                `${Accesses.getApiUrl()}/${editingId.value}/grant-all-users`,
            );

        const responseData = response?.data;
        const result = responseData?.data || {};

        const summaryParts = [];

        if (typeof result.processed_users === "number") {
            summaryParts.push(
                `${result.processed_users} users processados`,
            );
        }

        if (typeof result.local_access_granted === "number") {
            summaryParts.push(
                `${result.local_access_granted} novas associações locais`,
            );
        }

        if (typeof result.already_had_local_access === "number") {
            summaryParts.push(
                `${result.already_had_local_access} já tinham acesso`,
            );
        }

        if (typeof result.failed_users === "number") {
            summaryParts.push(
                `${result.failed_users} erros`,
            );
        }

        grantAllUsersSuccess.value = [
            responseData?.message
                || "Acesso atribuído aos users com sucesso.",

            summaryParts.length
                ? summaryParts.join(" · ")
                : "",
        ]
            .filter(Boolean)
            .join(" ");

        showGrantAllUsersConfirmation.value = false;
    } catch (error) {
        const responseData =
            error?.response?.data;

        grantAllUsersConfirmationError.value =
            responseData?.message
            || responseData?.error
            || "Ocorreu um erro ao dar acesso a todos os users.";

        grantAllUsersError.value =
            grantAllUsersConfirmationError.value;

        console.error(
            "Erro ao dar acesso a todos os users:",
            responseData || error,
        );
    } finally {
        grantingAccessToAllUsers.value = false;
    }
}

function formatDate(value) {
    if (! value) {
        return "";
    }

    return new Date(value)
        .toLocaleString("pt-PT");
}

async function fetchAppRoles(
    accessId,
    params = {},
) {
    const backend = getBackendValue();

    const resolvedAccessId =
        getAccessIdValue(accessId);

    if (
        ! backend
        || ! resolvedAccessId
    ) {
        clearAppRolesAndPermissions();
        return;
    }

    isFetchingAppRoles.value = true;
    isFetchingAppPermissions.value = true;

    try {
        const response = await AccessRoles.list(
            resolvedAccessId,
            params,
        );

        const rolesById =
            normalizeRolesResponse(
                response.data,
            );

        const roles =
            Object.values(rolesById);

        appPermissions.value = rolesById;

        expandedRoleGroups.value =
            Object.fromEntries(
                roles.map((role) => [
                    normalizeRoleId(role.id),
                    false,
                ]),
            );

        const selectedDefaultRoles = {};

        roles.forEach((role) => {
            if (
                ! normalizeBoolean(role.is_default)
                || ! role.id
            ) {
                return;
            }

            const roleId =
                normalizeRoleId(role.id);

            selectedDefaultRoles[roleId] = {
                id: roleId,
                name: role.name,
                display_name: role.display_name,
                permissions: role.permissions,
                is_default: true,
            };
        });

        formDefaultRoles.value =
            selectedDefaultRoles;
    } catch (error) {
        console.error(
            "Erro ao carregar roles da aplicação:",
            error?.response?.data || error,
        );

        clearAppRolesAndPermissions();
    } finally {
        isFetchingAppRoles.value = false;
        isFetchingAppPermissions.value = false;
    }
}

function clearAppRolesAndPermissions() {
    appPermissions.value = {};
    expandedRoleGroups.value = {};
    formDefaultRoles.value = {};
}

function isRoleSelected(roleId) {
    const normalizedRoleId =
        normalizeRoleId(roleId);

    if (! normalizedRoleId) {
        return false;
    }

    return Boolean(
        formDefaultRoles.value[
            normalizedRoleId
        ],
    );
}

function toggleDefaultRole(role, selected) {
    if (
        ! getBackendValue()
        || isFetchingAppPermissions.value
        || role.type !== "role"
    ) {
        return;
    }

    const roleId =
        normalizeRoleId(role.role_id);

    if (! roleId) {
        return;
    }

    const nextDefaultRoles = {
        ...formDefaultRoles.value,
    };

    if (normalizeBoolean(selected)) {
        nextDefaultRoles[roleId] = {
            id: roleId,
            name: role.name,
            display_name: role.display_name,
            permissions: role.permissions,
            is_default: true,
        };
    } else {
        delete nextDefaultRoles[roleId];
    }

    formDefaultRoles.value =
        nextDefaultRoles;
}

function isRoleGroupExpanded(roleId) {
    const normalizedRoleId =
        normalizeRoleId(roleId);

    return (
        expandedRoleGroups.value[
            normalizedRoleId
        ] !== false
    );
}

function toggleRoleGroup(roleId) {
    const normalizedRoleId =
        normalizeRoleId(roleId);

    if (! normalizedRoleId) {
        return;
    }

    expandedRoleGroups.value = {
        ...expandedRoleGroups.value,

        [normalizedRoleId]:
            ! isRoleGroupExpanded(
                normalizedRoleId,
            ),
    };
}

function getBackendValue() {
    return form.backend?.trim() || "";
}

function getAccessIdValue(accessId) {
    const resolvedAccessId =
        accessId
        ?? editingId.value
        ?? "";

    return String(resolvedAccessId)
        .trim();
}

function normalizeRolesResponse(payload) {
    const source = Array.isArray(payload)
        ? payload
        : (
            payload?.data
            ?? payload
            ?? []
        );

    const roles = Array.isArray(source)
        ? source
        : Object.values(source);

    return Object.fromEntries(
        roles
            .map(normalizeRole)
            .filter((role) => role?.id)
            .map((role) => {
                const normalizedRoleId =
                    normalizeRoleId(role.id);

                return [
                    normalizedRoleId,
                    {
                        ...role,
                        id: normalizedRoleId,
                    },
                ];
            }),
    );
}

function normalizeRole(role) {
    const permissions =
        Array.isArray(role?.permissions)
            ? role.permissions
                .map(normalizePermission)
                .filter(
                    (permission) =>
                        permission?.name,
                )
            : [];

    return {
        id: normalizeRoleId(role?.id),

        name:
            role?.name
            || "",

        display_name:
            role?.display_name
            || role?.name
            || "",

        permissions,

        is_default:
            normalizeBoolean(role?.is_default),
    };
}

function normalizePermission(permission) {
    return {
        id:
            permission?.id
            || permission?.name,

        name:
            permission?.name
            || "",

        display_name:
            permission?.display_name
            || permission?.name
            || "",
    };
}

function normalizeRoleId(roleId) {
    return String(roleId ?? "")
        .trim()
        .toLowerCase();
}

function normalizeBoolean(value) {
    if (value === true) {
        return true;
    }

    if (
        value === false
        || value === null
        || value === undefined
    ) {
        return false;
    }

    if (typeof value === "number") {
        return value === 1;
    }

    if (typeof value === "string") {
        return [
            "1",
            "true",
            "yes",
            "on",
        ].includes(
            value
                .trim()
                .toLowerCase(),
        );
    }

    return false;
}

async function openManageRolesDialog(access = {}) {
    await dialog.open({
        component: AccessRolesManagementTable,

        props: {
            access,
            accessId: access?.id || "",
        },

        options: {
            title: `Gerir roles da aplicação: ${
                access?.name
                || access?.id
                || ""
            }`,

            titleBackgroundColor: "white",
            titleTextColor: "black",
            maxWidth: "1200px",
            showActions: false,
        },
    });
}
</script>

<style scoped>
.admin-page {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.users-wrapper {
  margin-top: 8px;
}

.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.admin-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
}

.dialog-card {
  border-radius: 18px;
}

.access-dialog-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.confirmation-text {
  margin: 0;
  color: #334155;
  font-size: 1rem;
  line-height: 1.6;
}

.submit-error {
  margin-top: 12px;
  color: #dc2626;
  font-size: 0.95rem;
}

.empty-state {
  text-align: center;
  padding: 40px;
}

.empty-icon {
  width: 40px;
  height: 40px;
  background: #e2e8f0;
  border-radius: 50%;
  margin: 0 auto 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.permissions-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
}

.permissions-table {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.permissions-table--disabled {
  opacity: 0.62;
  pointer-events: none;
}

.permissions-role-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.permissions-toggle {
  flex: 0 0 auto;
}

.permissions-role-name,
.permissions-role-display-name {
  color: #0f172a;
  font-weight: 600;
}

.permissions-permission-name,
.permissions-permission-display-name {
  color: #64748b;
}

.permissions-permission-name {
  display: inline-block;
  padding-left: 24px;
}

.permissions-empty-state {
  padding: 24px;
}

.v-data-table,
.v-table,
.v-data-table-header,
.v-data-footer {
  background: transparent !important;
}

.v-data-table th {
  font-weight: 600;
  color: #1e293b;
}

.v-data-table td {
  color: #475569;
}

@media (max-width: 700px) {
  .access-dialog-actions {
    flex-wrap: wrap;
  }

  .access-dialog-actions .v-btn:first-child {
    width: 100%;
  }
}
</style>